import { ReactNode } from "react";
import { type SeoCluster } from "@shared/config/seo-pages";

export interface CatalogConfig {
  cluster: SeoCluster;
  path: string;
  metaTitle: string;
  metaDescription: string;
  breadcrumb: string;
  title: ReactNode;
  subtitle: string;
  intro: ReactNode;
  listHeading: string;
  faq: { question: string; answer: string }[];
}

export const FORMATS_CATALOG: CatalogConfig = {
  cluster: "format",
  path: "/formats",
  metaTitle: "Конвертер аудио и видео в текст — все форматы | Polotno",
  metaDescription:
    "Расшифровка аудио и видео в текст для всех популярных форматов: MP3, WAV, M4A, MP4, MOV, OGG, WEBM и других. Загружайте файл как есть, без конвертации.",
  breadcrumb: "Форматы файлов",
  title: (
    <>
      <span className="text-primary font-rublev">К</span>онвертируйте любой формат в текст
    </>
  ),
  subtitle: "Аудио и видео любых форматов — загружайте файл как есть, конвертировать ничего не нужно.",
  intro: (
    <>
      <p>
        Форматов звука и видео десятки, и почти каждый инструмент записи использует свой: смартфон снимает в MOV или
        MP4, мессенджеры сохраняют голосовые в OGG, диктофоны пишут в M4A или WMA, браузер выгружает WEBM. Обычно это
        превращается в лишний шаг — поиск конвертера перед тем, как что-то сделать с записью.
      </p>
      <p>
        Здесь этот шаг не нужен. Сервис сам определяет формат, находит внутри контейнера аудиодорожку и работает с ней,
        а видеоряд игнорирует — для расшифровки он не требуется. Ограничения одинаковы для всех форматов: до 2 ГБ на
        файл и до четырёх часов записи.
      </p>
      <p>
        На точность влияет не расширение файла, а качество исходной записи: разборчивость речи, уровень фонового шума и
        расстояние до микрофона. Несжатый WAV даёт небольшое преимущество, но на чистой записи разница с MP3 почти
        незаметна.
      </p>
    </>
  ),
  listHeading: "Расшифровка по форматам",
  faq: [
    {
      question: "Нужно ли конвертировать файл перед загрузкой?",
      answer:
        "Нет. Формат определяется автоматически, аудиодорожка извлекается из видеоконтейнеров тоже автоматически. Конвертация потребуется только если файл превышает 2 ГБ."
    },
    {
      question: "Какой формат даёт лучшую точность?",
      answer:
        "Формально — несжатый WAV, так как в нём нет потерь от кодирования. На практике разница с MP3 или AAC хорошего битрейта минимальна: гораздо важнее качество самой записи."
    },
    {
      question: "Что делать, если файл больше 2 ГБ?",
      answer:
        "Извлеките из видео только аудиодорожку или пересохраните запись в MP3 с битрейтом от 128 кбит/с. Размер сократится в разы, а точность распознавания практически не изменится."
    },
    {
      question: "Обязательно ли загружать файл?",
      answer:
        "Нет. Если запись опубликована на YouTube, VK Video или Rutube, достаточно вставить ссылку — сервис сам заберёт аудиодорожку."
    }
  ]
};

export const PLATFORMS_CATALOG: CatalogConfig = {
  cluster: "platform",
  path: "/platforms",
  metaTitle: "Расшифровка записей созвонов и видео — все платформы | Polotno",
  metaDescription:
    "Расшифровка записей из Zoom, Google Meet, Teams, Телемоста, VK Звонков и видео с YouTube, VK Video и Rutube. Файлом или по ссылке.",
  breadcrumb: "Платформы и источники",
  title: (
    <>
      <span className="text-primary font-rublev">О</span>ткуда угодно — в текст
    </>
  ),
  subtitle: "Запись созвона или ролик с видеохостинга: загрузите файл или вставьте ссылку.",
  intro: (
    <>
      <p>
        Способов получить запись два, и выбор между ними зависит от того, где она лежит. Ролики, опубликованные
        открыто на YouTube, VK Video или Rutube, достаточно указать ссылкой — сервис заберёт аудиодорожку сам, ничего
        скачивать не нужно.
      </p>
      <p>
        Записи созвонов из Zoom, Google Meet, Teams, Телемоста и подобных платформ так не работают: они лежат в
        облаках, закрытых вашей авторизацией, и внешнему сервису недоступны. Такой файл нужно скачать и загрузить —
        зато конвертировать его не придётся, формат определяется автоматически.
      </p>
      <p>
        Дальше всё одинаково независимо от источника: текст приходит с пунктуацией, таймкодами и разделением по
        спикерам, а выгрузить его можно в TXT, DOCX, PDF или SRT.
      </p>
    </>
  ),
  listHeading: "Расшифровка по платформам",
  faq: [
    {
      question: "Когда можно обойтись ссылкой вместо файла?",
      answer:
        "Если запись открыто опубликована на YouTube, VK Video или Rutube. Всё остальное — включая облачные записи созвонов — нужно загружать файлом."
    },
    {
      question: "Почему нельзя дать ссылку на облачную запись созвона?",
      answer:
        "Такие записи защищены авторизацией в вашем аккаунте, и внешний сервис до них не дотянется. Скачайте файл и загрузите его."
    },
    {
      question: "Нужно ли извлекать звук из видеозаписи?",
      answer: "Нет, это происходит автоматически. Видеоряд для расшифровки не используется и просто игнорируется."
    },
    {
      question: "Есть ли разница в точности между источниками?",
      answer:
        "Разницы из-за платформы нет — важно только качество звука. Гарнитура у каждого участника даёт заметно лучший результат, чем общий микрофон в переговорке."
    }
  ]
};

export const USE_CASES_CATALOG: CatalogConfig = {
  cluster: "usecase",
  path: "/use-cases",
  metaTitle: "Расшифровка аудио в текст для разных задач | Polotno",
  metaDescription:
    "Расшифровка записей для совещаний, интервью, лекций, собеседований, исследований и звонков. Готовые сценарии под конкретную задачу.",
  breadcrumb: "Задачи",
  title: (
    <>
      <span className="text-primary font-rublev">Р</span>асшифровка под вашу задачу
    </>
  ),
  subtitle: "Совещание, интервью, лекция или исследование — у каждого сценария свои требования к тексту.",
  intro: (
    <>
      <p>
        Технически расшифровка везде одна и та же, а вот нужный результат отличается сильно. Для протокола совещания
        важнее всего разделение по спикерам и краткая сводка. Для научного интервью — дословность и возможность
        сослаться на момент в записи. Для лекции — конспект, из которого потом учат.
      </p>
      <p>
        Поэтому имеет смысл смотреть не только на сам текст, но и на то, что с ним делать дальше: выгружать в DOCX для
        правки, в SRT для субтитров или сразу получать AI-сводку с ключевыми моментами.
      </p>
      <p>
        Ниже — сценарии, разобранные по отдельности: что именно даёт расшифровка в каждом случае и на что обратить
        внимание при записи, чтобы результат получился пригодным для работы.
      </p>
    </>
  ),
  listHeading: "Расшифровка по задачам",
  faq: [
    {
      question: "Отличается ли обработка для разных задач?",
      answer:
        "Сама расшифровка одинаковая. Отличается то, что с ней удобнее делать дальше: сводка, выгрузка в документ или субтитры."
    },
    {
      question: "Что сильнее всего влияет на пригодность результата?",
      answer:
        "Качество исходной записи: разборчивость речи, уровень шума и расстояние до микрофона. Это важнее выбора формата и настроек."
    },
    {
      question: "Нужно ли вычитывать расшифровку?",
      answer:
        "Зависит от задачи. Для конспекта обычно нет, для цитат в научной работе или юридических формулировок — обязательно."
    },
    {
      question: "Можно ли обойтись без полного текста?",
      answer:
        "Да, AI-сводка даёт краткое изложение и ключевые моменты. Полный текст при этом остаётся доступен, если понадобятся детали."
    }
  ]
};

export const EXPORTS_CATALOG: CatalogConfig = {
  cluster: "export",
  path: "/exports",
  metaTitle: "Форматы выгрузки расшифровки — Word, PDF, SRT | Polotno",
  metaDescription:
    "Выгрузка расшифровки в TXT, DOCX, PDF и SRT, таймкоды, разделение по спикерам и AI-сводка. Выберите формат уже после обработки записи.",
  breadcrumb: "Форматы результата",
  title: (
    <>
      <span className="text-primary font-rublev">Ч</span>то получится на выходе
    </>
  ),
  subtitle: "Документ, субтитры или краткая сводка — формат выбирается после расшифровки.",
  intro: (
    <>
      <p>
        Расшифровка делается один раз, а вот забрать её можно в разном виде — и решать это заранее не нужно. DOCX
        подойдёт, если текст будут править, PDF — если передавать без изменений, SRT — если нужны субтитры к видео,
        TXT — если важен чистый текст без разметки.
      </p>
      <p>
        Независимо от формата в результате сохраняется структура: реплики сгруппированы по говорящим, к ним привязаны
        таймкоды, текст разбит на абзацы. Это то, что отличает пригодную для работы расшифровку от сплошного потока
        слов.
      </p>
      <p>
        Отдельная опция — AI-сводка. Она не заменяет полный текст, а идёт рядом с ним: краткое изложение и ключевые
        моменты для тех случаев, когда десять страниц расшифровки читать некому.
      </p>
    </>
  ),
  listHeading: "Форматы и возможности выгрузки",
  faq: [
    {
      question: "Нужно ли выбирать формат до расшифровки?",
      answer: "Нет. Обработка делается один раз, а скачать результат можно в любом формате и в любом количестве."
    },
    {
      question: "Сохраняются ли спикеры и таймкоды при выгрузке?",
      answer: "Да, во всех форматах, кроме чистого TXT, где остаётся только текст."
    },
    {
      question: "Какой формат выбрать для субтитров?",
      answer: "SRT — он подключается к видео как дорожка субтитров и содержит готовые тайминги."
    },
    {
      question: "Чем сводка отличается от расшифровки?",
      answer:
        "Расшифровка передаёт речь целиком, сводка пересказывает содержание. Для точных формулировок нужен полный текст."
    }
  ]
};

export const LANGUAGES_CATALOG: CatalogConfig = {
  cluster: "language",
  path: "/languages",
  metaTitle: "Расшифровка речи в текст на разных языках | Polotno",
  metaDescription:
    "Транскрибация речи в текст на русском, английском, казахском, узбекском, армянском, грузинском и других языках. Автоопределение языка и разделение по спикерам.",
  breadcrumb: "Языки",
  title: (
    <>
      <span className="text-primary font-rublev">Р</span>асшифровка речи на разных языках
    </>
  ),
  subtitle: "Выберите язык записи или доверьте определение автоматике — текст придёт с пунктуацией и таймкодами.",
  intro: (
    <>
      <p>
        Сервис распознаёт речь на двух десятках языков, включая те, для которых готовых инструментов почти нет:
        казахский, узбекский, армянский, грузинский. Если язык записи заранее неизвестен или в разговоре их несколько,
        подойдёт режим автоопределения — система разберётся сама.
      </p>
      <p>
        Один нюанс, о котором стоит знать заранее: языки Центральной Азии и Кавказа поддерживает только модель
        повышенной точности, поэтому для них нужно выбрать режим «Качество». Для русского, английского и других
        распространённых языков работают оба режима — быстрый обрабатывает запись заметно скорее.
      </p>
      <p>
        Независимо от языка результат приходит с автоматической пунктуацией, разделением по спикерам и таймкодами, а
        выгрузить его можно в TXT, DOCX, PDF или SRT.
      </p>
    </>
  ),
  listHeading: "Расшифровка по языкам",
  faq: [
    {
      question: "Что делать, если язык записи заранее неизвестен?",
      answer:
        "Выберите в списке «Автоопределение» — система сама распознает язык по первым секундам записи и обработает её соответствующим образом."
    },
    {
      question: "Почему для некоторых языков доступен только режим «Качество»?",
      answer:
        "Быстрый и точный режимы работают на разных моделях распознавания. Казахский, узбекский, армянский и грузинский поддерживает только модель повышенной точности, поэтому для них режим переключается автоматически."
    },
    {
      question: "Как обрабатывается речь, в которой смешаны два языка?",
      answer:
        "Такие записи распознаются, но на переключениях между языками возможны отдельные неточности. Для смешанной речи стоит попробовать режим автоопределения и сравнить результат."
    },
    {
      question: "Переводится ли текст на русский?",
      answer:
        "Нет, расшифровка выполняется на языке оригинала. Готовый текст можно перевести любым переводчиком — это гораздо проще, чем переводить со слуха."
    }
  ]
};
